// Shown by ProtectedRoute to any authenticated user whose access hasn't been
// granted yet (status !== "active"). Lets them submit / view an access request.
import { useEffect, useState } from 'react';
import { apiV1 } from '@/api/client';
import { useAuth } from '@/contexts/AuthContext';

const ROLES = ['viewer', 'editor', 'admin'];

export default function AccessPending() {
  const { user, logout } = useAuth();
  const [existing, setExisting] = useState(undefined); // undefined = loading
  const [reason, setReason] = useState('');
  const [requestedRole, setRequestedRole] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const denied = user?.status === 'denied';

  useEffect(() => {
    let alive = true;
    apiV1
      .get('/access-requests/mine')
      .then((r) => alive && setExisting(r.data?.request || null))
      .catch(() => alive && setExisting(null));
    return () => {
      alive = false;
    };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const r = await apiV1.post('/access-requests', {
        requested_role: requestedRole || null,
        reason,
      });
      setExisting(r.data?.request || { status: 'pending' });
    } catch (err) {
      setError(err?.data?.detail?.message || err?.message || 'Could not submit request');
    } finally {
      setSubmitting(false);
    }
  };

  const pending = existing && existing.status === 'pending';

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A1410] text-[#e7edf3] p-6">
      <div className="w-full max-w-md rounded-xl border border-[#1f3a2e] bg-[#10241a] p-8">
        <p className="text-[10px] tracking-[0.3em] uppercase text-[#8fb3a2]">Pam Golding Properties</p>
        <h1 className="mt-2 text-2xl font-light" style={{ fontFamily: 'Montserrat, Arial, sans-serif', textTransform: 'uppercase', letterSpacing: '0.12em' }}>LinkHub</h1>
        <div className="my-5 h-px w-10 bg-[#2f6e54]" />

        <p className="text-sm text-[#8aa0b4]">
          Signed in as <strong className="text-[#e7edf3]">{user?.full_name || user?.email}</strong>.
        </p>

        {existing === undefined ? (
          <p className="mt-4 text-sm text-[#8aa0b4]">Loading…</p>
        ) : pending ? (
          <p className="mt-4 text-sm text-[#d9b54a]">
            Your access request is <strong>pending review</strong>. You'll get access once an
            administrator approves it.
          </p>
        ) : denied ? (
          <p className="mt-4 text-sm text-[#e0726b]">
            Your access request was declined. Please contact your administrator.
          </p>
        ) : (
          <form onSubmit={submit} className="mt-4">
            <p className="text-sm text-[#8aa0b4]">
              You're authenticated but don't yet have access to LinkHub. Request it below.
            </p>
            <label className="mt-4 block text-xs text-[#8aa0b4]">
              Requested role
              <select
                value={requestedRole}
                onChange={(e) => setRequestedRole(e.target.value)}
                className="mt-1 w-full rounded bg-[#0A1410] border border-[#1f3a2e] p-2 text-sm text-[#e7edf3]"
              >
                <option value="">(let the admin decide)</option>
                {ROLES.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </label>
            <label className="mt-3 block text-xs text-[#8aa0b4]">
              Reason (optional)
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                placeholder="Why do you need access?"
                className="mt-1 w-full rounded bg-[#0A1410] border border-[#1f3a2e] p-2 text-sm text-[#e7edf3]"
              />
            </label>
            {error && <p className="mt-2 text-sm text-[#e0726b]">{error}</p>}
            <button
              type="submit"
              disabled={submitting}
              className="mt-4 rounded-lg bg-[#144733] px-4 py-2 text-sm font-medium text-white disabled:opacity-60"
            >
              {submitting ? 'Submitting…' : 'Request access'}
            </button>
          </form>
        )}

        <div className="mt-6 flex items-center gap-4">
          <button
            onClick={() => window.location.reload()}
            className="text-sm text-[#8fb3a2] hover:underline"
          >
            Check access
          </button>
          <button onClick={logout} className="text-sm text-[#6a8097] hover:underline">
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
}
